import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LifeBuoy, MessageCircle, Mail, BookOpen } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import {
  SidebarMenuButton,
} from '@/components/ui/sidebar';
import { useAuth } from '@/contexts/AuthContext';
import { useFocusedPatient } from '@/hooks/useFocusedPatient';
import { supabase } from '@/integrations/supabase/client';
import { captureEvent } from '@/lib/posthog';

type Variant = 'sidebar' | 'icon' | 'inline' | 'menu-items';

interface SupportMenuProps {
  variant?: Variant;
}

const SUPPORT_WHATSAPP = (import.meta.env.VITE_SUPPORT_WHATSAPP || '').replace(/[^\d+]/g, '');
const SUPPORT_EMAIL = import.meta.env.VITE_SUPPORT_EMAIL || 'help@careos.life';

function useSupportLinks() {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const { focusedPatient } = useFocusedPatient();

  const { data: profile } = useQuery({
    // Distinct key: this query selects only full_name, and sharing the
    // ['profile', id] key would starve other consumers of avatar_url.
    queryKey: ['profile-name', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('user_id', user.id)
        .maybeSingle();
      return data;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const hasWhatsApp = !!SUPPORT_WHATSAPP;
  const hasEmail = !!SUPPORT_EMAIL;

  const name = profile?.full_name || user?.email || t('common.user');
  const email = user?.email || '';
  const route = location.pathname;
  const patient = focusedPatient?.nickname || '-';
  const message = t('support.prefilledMessage', { name, email, route, patient });

  const whatsappNumber = SUPPORT_WHATSAPP.replace(/^\+/, '');
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
  const emailSubject = t('support.emailSubject');
  const emailUrl = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(message)}`;

  const open = (channel: 'whatsapp' | 'email') => {
    try {
      captureEvent('support_channel_clicked', { channel, route });
    } catch {
      /* noop */
    }
    if (channel === 'email') {
      window.location.href = emailUrl;
    } else {
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return { hasWhatsApp, hasEmail, open };
}

export function SupportMenu({ variant = 'icon' }: SupportMenuProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasWhatsApp, hasEmail, open } = useSupportLinks();

  const goToHelp = () => navigate('/app/help');
  // Help center is always available, even if no contact channels configured

  // Renders items directly (to be used INSIDE another DropdownMenu, e.g. mobile hamburger)
  if (variant === 'menu-items') {
    return (
      <>
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
          {t('support.title')}
        </DropdownMenuLabel>
        <DropdownMenuItem onSelect={goToHelp}>
          <BookOpen className="w-4 h-4 mr-2" />
          {t('help.title')}
        </DropdownMenuItem>
        {hasWhatsApp && (
          <DropdownMenuItem onSelect={() => open('whatsapp')}>
            <MessageCircle className="w-4 h-4 mr-2" />
            {t('support.whatsapp')}
          </DropdownMenuItem>
        )}
        {hasEmail && (
          <DropdownMenuItem onSelect={() => open('email')}>
            <Mail className="w-4 h-4 mr-2" />
            {t('support.email')}
          </DropdownMenuItem>
        )}
      </>
    );
  }

  const trigger =
    variant === 'sidebar' ? (
      <SidebarMenuButton tooltip={t('support.title')} className="w-full">
        <LifeBuoy className="h-5 w-5" />
        <span>{t('support.title')}</span>
      </SidebarMenuButton>
    ) : (
      <Button variant="ghost" size="icon" className="shrink-0" aria-label={t('support.title')}>
        <LifeBuoy className="w-5 h-5" />
      </Button>
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>{t('support.contactUs')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={goToHelp}>
          <BookOpen className="w-4 h-4 mr-2" />
          {t('help.title')}
        </DropdownMenuItem>
        {hasWhatsApp && (
          <DropdownMenuItem onSelect={() => open('whatsapp')}>
            <MessageCircle className="w-4 h-4 mr-2" />
            {t('support.whatsapp')}
          </DropdownMenuItem>
        )}
        {hasEmail && (
          <DropdownMenuItem onSelect={() => open('email')}>
            <Mail className="w-4 h-4 mr-2" />
            {t('support.email')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
